// features/common/PhotosComponent.js
// Reusable photo upload and management component
// Can be used in DEL, Safety Audit, Near Miss, and other forms

import React, { useRef, useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import { PhotoViewModal } from "./PhotoViewModal";
import { CameraModal } from "./CameraModal";
import { CachedImage } from "./CachedImage";
import { FormPhotos } from "../../models/FormPhotos/FormPhotos";
import { burnInMetadata } from "../../services/photoExportService";
import styles from "./PhotosComponent.module.css";

// Safely get display URL from a photo, whether it's a FormPhotos instance or a plain object
const getPhotoUrl = (photo) => {
  if (typeof photo.getDisplayUrl === "function") {
    return photo.getDisplayUrl();
  }
  return photo.localPreviewUrl || photo.presignedUrl || photo.presigned_url || null;
};

/**
 * PhotosComponent - Reusable photo upload and management component
 *
 * @param {object} props
 * @param {Array} props.photos - Array of FormPhotos instances (already uploaded)
 * @param {object} props.photosUI - UI state from store (selectedPhotos, isUploading, etc.)
 * @param {object} props.photosActions - Actions from makePhotosActions
 * @param {function} props.emitPatch - Parent form's WebSocket emit function
 * @param {boolean} props.isViewMode - Whether the form is in view-only mode
 * @param {string} props.connectionStatus - WebSocket connection status
 * @param {boolean} props.canDownload - Whether user can download photos
 * @param {string} props.formId - Parent form UUID (del_id, safety_audit_id, etc.)
 * @param {string} props.formType - Type of form ("del", "safety_audit", etc.)
 * @param {function} props.showLoading - Show loading modal function
 * @param {function} props.hideLoading - Hide loading modal function
 * @param {function} props.showNotification - Show notification function
 */
export const PhotosComponent = ({
  photos = [],
  photosUI = {},
  photosActions,
  emitPatch,
  isViewMode = false,
  connectionStatus,
  canDownload = false,
  formId,
  formType,
  showLoading,
  hideLoading,
  showNotification,
}) => {
  const fileInputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [showDownloadAllChoice, setShowDownloadAllChoice] = useState(false);

  const {
    selectedPhotos = [],
    isUploading = false,
    uploadError = null,
    showDeleteConfirm = false,
    photoToDelete = null,
    viewModalOpen = false,
    viewingPhoto = null,
  } = photosUI;

  const isOffline = connectionStatus !== "connected";

  // Handle file input change
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await photosActions.addPendingPhotos(files);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  // Handle add photos button click
  const handleAddPhotosClick = () => {
    fileInputRef.current?.click();
  };

  // Handle camera capture
  const handleCameraCapture = async ({ blob, geoMetadata }) => {
    const file = new File([blob], `camera_${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    const photo = await FormPhotos.fromCameraCapture(file, geoMetadata);
    photosActions.addPendingPhotosWithGeo([photo]);
  };

  // Handle upload photos
  const handleUploadPhotos = async () => {
    if (isOffline) {
      showNotification({
        type: "error",
        title: "Offline",
        message:
          "Cannot upload photos while offline. Please reconnect and try again.",
      });
      return;
    }

    showLoading("Uploading photos...");
    const res = await photosActions.uploadPendingPhotos(
      emitPatch,
      formId,
      formType,
    );
    hideLoading();

    if (res.ok) {
      showNotification({
        type: "success",
        title: "Success",
        message: "Photos uploaded successfully.",
      });
    } else {
      showNotification({
        type: "error",
        title: "Upload Failed",
        message: res.error || "Failed to upload photos.",
      });
    }
  };

  // Handle delete photo confirmation
  const handleConfirmDelete = async () => {
    if (isOffline) {
      showNotification({
        type: "error",
        title: "Offline",
        message:
          "Cannot delete photos while offline. Please reconnect and try again.",
      });
      photosActions.hideDeleteConfirm();
      return;
    }

    showLoading("Deleting photo...");
    const res = await photosActions.deletePhoto(
      photoToDelete,
      emitPatch,
      formId,
      formType,
    );
    hideLoading();

    if (res.ok) {
      showNotification({
        type: "success",
        title: "Success",
        message: "Photo deleted successfully.",
      });
    } else {
      showNotification({
        type: "error",
        title: "Delete Failed",
        message: res.error || "Failed to delete photo.",
      });
    }
  };

  // Handle caption change on uploaded photos (local only)
  const handleCaptionChange = (photoId, caption) => {
    photosActions.updatePhotoCaption(photoId, caption, null, formId, formType);
  };

  // Handle caption blur on uploaded photos (sync to server)
  const handleCaptionBlur = async (photoId, caption) => {
    if (isOffline) return;

    const res = await photosActions.updatePhotoCaption(
      photoId,
      caption,
      emitPatch,
      formId,
      formType,
    );

    if (res && !res.ok) {
      showNotification({
        type: "error",
        title: "Save Failed",
        message: res.error || "Failed to save caption.",
      });
    }
  };

  // Handle photo click (open view modal)
  const handlePhotoClick = (photo) => {
    photosActions.openViewModal(photo);
  };

  // Handle download photo
  const handleDownloadPhoto = async (photo) => {
    const res = await photosActions.downloadPhoto(photo);
    if (!res.ok) {
      showNotification({
        type: "error",
        title: "Download Failed",
        message: res.error || "Failed to download photo.",
      });
    }
  };

  // Handle download all photos - show choice if any photo has geo data
  const handleDownloadAll = () => {
    if (photos.length === 0) return;

    const anyHasGeo = photos.some(
      (p) => typeof p.hasGeoData === "function" && p.hasGeoData(),
    );

    if (anyHasGeo) {
      setShowDownloadAllChoice(true);
    } else {
      downloadAllOriginals();
    }
  };

  // Download all photos as originals only
  const downloadAllOriginals = async () => {
    setShowDownloadAllChoice(false);
    if (photos.length === 0) return;

    showLoading(
      `Downloading ${photos.length} photo${photos.length > 1 ? "s" : ""}...`,
    );

    let successCount = 0;
    let failCount = 0;

    try {
      for (const photo of photos) {
        const res = await photosActions.downloadPhoto(photo);
        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }
    } catch (err) {
      console.error("Download all originals error:", err);
    } finally {
      hideLoading();
      showDownloadNotification(successCount, failCount);
    }
  };

  // Download all photos with both versions (original + burn-in for geo photos)
  const downloadAllBothVersions = async () => {
    setShowDownloadAllChoice(false);
    if (photos.length === 0) return;

    showLoading("Downloading photos with location data...");

    let successCount = 0;
    let failCount = 0;

    try {
      for (const photo of photos) {
        // Always download original
        const res = await photosActions.downloadPhoto(photo);
        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }

        // Also download burn-in version for geo-tagged photos
        const hasGeo =
          typeof photo.hasGeoData === "function" && photo.hasGeoData();
        if (hasGeo) {
          try {
            const url =
              typeof photo.getDisplayUrl === "function"
                ? photo.getDisplayUrl()
                : photo.localPreviewUrl ||
                  photo.presignedUrl ||
                  photo.presigned_url ||
                  null;

            if (url) {
              const blob = await burnInMetadata(url, {
                latitude: photo.latitude,
                longitude: photo.longitude,
                altitude: photo.altitude,
                bearing: photo.bearing,
                accuracy: photo.accuracy,
                address: photo.address,
                capturedAt: photo.capturedAt,
                capturedAtLocal: photo.capturedAtLocal,
              });

              const blobUrl = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = blobUrl;
              a.download = `photo_${photo.id}_geo.jpg`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(blobUrl);
              successCount++;
            }
          } catch (err) {
            console.error("Burn-in export failed for photo:", photo.id, err);
            failCount++;
          }
        }
      }
    } catch (err) {
      console.error("Download all both versions error:", err);
    } finally {
      hideLoading();
      showDownloadNotification(successCount, failCount);
    }
  };

  // Shared download notification helper
  const showDownloadNotification = (successCount, failCount) => {
    if (failCount === 0) {
      showNotification({
        type: "success",
        title: "Download Complete",
        message: `Successfully downloaded ${successCount} file${successCount > 1 ? "s" : ""}.`,
      });
    } else if (successCount === 0) {
      showNotification({
        type: "error",
        title: "Download Failed",
        message: "Failed to download photos. Please try again.",
      });
    } else {
      showNotification({
        type: "warning",
        title: "Partial Download",
        message: `Downloaded ${successCount} file${successCount > 1 ? "s" : ""}, ${failCount} failed.`,
      });
    }
  };

  return (
    <div className={styles.photosContainer}>
      {/* Offline warning banner */}
      {isOffline && !isViewMode && (
        <div className={styles.offlineWarning}>
          <span className={styles.warningIcon}>!</span>
          <span>
            You are currently offline. Photo uploads require an internet
            connection.
          </span>
        </div>
      )}

      {isOffline && isViewMode && (
        <div className={styles.offlineWarning}>
          <span className={styles.warningIcon}>!</span>
          <span>
            You are in view-only mode. You are unable to upload or modify photos
            in this mode.
          </span>
        </div>
      )}

      {/* Error banner */}
      {uploadError && (
        <div className={styles.errorBanner}>
          <span>{uploadError}</span>
          <button
            className={styles.errorDismiss}
            onClick={() => photosActions.clearUploadError()}
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Actions section */}
      {(!isViewMode || (canDownload && photos.length > 0)) && (
        <div className={styles.addSection}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            multiple
            style={{ display: "none" }}
          />
          {!isViewMode && (
            <button
              className={styles.addButton}
              onClick={handleAddPhotosClick}
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Add Photos
            </button>
          )}
          {!isViewMode && (
            <button
              className={styles.addButton}
              onClick={() => setCameraOpen(true)}
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              Take Photo
            </button>
          )}
          {canDownload && photos.length > 0 && (
            <button
              className={styles.downloadAllButton}
              onClick={handleDownloadAll}
              type="button"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download All
            </button>
          )}
        </div>
      )}

      {/* Pending uploads section */}
      {selectedPhotos.length > 0 && (
        <div className={styles.pendingSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>
              Selected Photos ({selectedPhotos.length})
            </h4>
            <div className={styles.pendingActions}>
              <button
                className={styles.clearButton}
                onClick={() => photosActions.clearPendingPhotos()}
                type="button"
              >
                Clear All
              </button>
              <button
                className={styles.uploadButton}
                onClick={handleUploadPhotos}
                disabled={isUploading || isOffline}
                type="button"
              >
                {isUploading ? "Uploading..." : "Save Photos"}
              </button>
            </div>
          </div>

          <div className={styles.photoGrid}>
            {selectedPhotos.map((photo) => (
              <div key={photo.id} className={styles.photoCard}>
                <div className={styles.photoImageContainer}>
                  <img
                    src={getPhotoUrl(photo)}
                    alt={photo.caption || "Pending photo"}
                    className={styles.photoImage}
                  />
                  <button
                    className={styles.removeButton}
                    onClick={() => photosActions.removePendingPhoto(photo.id)}
                    type="button"
                    title="Remove photo"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <input
                  type="text"
                  className={styles.captionInput}
                  placeholder="Add caption..."
                  value={photo.caption || ""}
                  onChange={(e) =>
                    photosActions.updatePendingPhotoCaption(
                      photo.id,
                      e.target.value,
                    )
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded photos section */}
      {photos.length > 0 && (
        <div className={styles.uploadedSection}>
          <h4 className={styles.sectionTitle}>
            Uploaded Photos ({photos.length})
          </h4>

          <div className={styles.photoGrid}>
            {photos.map((photo) => (
              <div key={photo.id} className={styles.photoCard}>
                <div
                  className={styles.photoImageContainer}
                  onClick={() => handlePhotoClick(photo)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) =>
                    e.key === "Enter" && handlePhotoClick(photo)
                  }
                >
                  <CachedImage
                    photoId={photo.id}
                    src={getPhotoUrl(photo)}
                    alt={photo.caption || "Uploaded photo"}
                    className={styles.photoImage}
                  />
                  <div className={styles.photoOverlay}>
                    <span className={styles.viewText}>Click to view</span>
                  </div>
                </div>

                {/* Caption display with edit hint */}
                <div
                  className={styles.captionRow}
                  onClick={() => handlePhotoClick(photo)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) =>
                    e.key === "Enter" && handlePhotoClick(photo)
                  }
                >
                  {photo.caption ? (
                    <p className={styles.captionText}>{photo.caption}</p>
                  ) : !isViewMode ? (
                    <span className={styles.captionHint}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Add caption
                    </span>
                  ) : null}
                </div>

                {/* Action buttons */}
                <div className={styles.photoActions}>
                  {canDownload && (
                    <button
                      className={styles.actionButton}
                      onClick={() => handleDownloadPhoto(photo)}
                      type="button"
                      title="Download photo"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  )}
                  {!isViewMode && (
                    <button
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                      onClick={() => photosActions.showDeleteConfirm(photo.id)}
                      disabled={isOffline}
                      type="button"
                      title="Delete photo"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {photos.length === 0 && selectedPhotos.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <p className={styles.emptyText}>No photos added yet.</p>
          {!isViewMode && (
            <p className={styles.emptySubtext}>
              Click "Add Photos" to attach photos to this form.
            </p>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onCancel={() => photosActions.hideDeleteConfirm()}
        onConfirm={handleConfirmDelete}
        title="Delete Photo"
        message="Are you sure you want to delete this photo? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="deleteButton"
      />

      {/* Download all choice modal */}
      <ConfirmationModal
        isOpen={showDownloadAllChoice}
        onCancel={downloadAllOriginals}
        onConfirm={downloadAllBothVersions}
        title="Download All Photos"
        message="Some photos have location data. Choose a download option:"
        cancelText="Originals Only"
        confirmText="Both Versions"
        confirmButtonClass="confirmButton"
      />

      {/* Camera modal */}
      <CameraModal
        isOpen={cameraOpen}
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
      />

      {/* Photo view modal */}
      <PhotoViewModal
        isOpen={viewModalOpen}
        photo={viewingPhoto}
        onClose={() => photosActions.closeViewModal()}
        canDownload={canDownload}
        onDownload={handleDownloadPhoto}
        isViewMode={isViewMode}
        onCaptionChange={handleCaptionChange}
        onCaptionBlur={handleCaptionBlur}
      />
    </div>
  );
};

export default PhotosComponent;
